import { Context } from 'js/components/LocaleWrapper';
import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ColorBloomPurple100,
  ColorBloomPurple700,
  ColorBloomTeal100,
  ColorBloomTeal700,
  ColorBloomYellow100,
  ColorBloomYellow700,
} from '@treatwell/design-tokens';
import {
  IconHappy,
  IconHair,
  IconVoucher,
  Text,
  buttonStyles,
  ReferralPage as TWUIReferralPage,
  IconAdd,
  useDevice,
} from '@treatwell/ui';
import { formatPrice } from 'js/helpers/price/priceUtilities';
import clsx from 'clsx';

import { useTrackPageAndSessionView } from 'js/hooks/useTrackPageAndSessionView';
import styles from './ReferralPage.module.css';
import { trackRedeemCredit } from './tracking';

const REFERRAL_TC_PATH = '/info/tw-referral-t-c';

const FAQBody = () => {
  return (
    <Trans i18nKey="referral.faqs.description">
      <Text as="p">
        The rules and limitations set out in this FAQ also apply to the{' '}
        <a href={REFERRAL_TC_PATH} data-cy="referral-tc-link">
          <Text type="bodyHeavy">Referral Program's Terms and Conditions</Text>
        </a>
      </Text>
    </Trans>
  );
};

const RedeemAction = () => {
  const { t } = useTranslation();
  const { pageData } = useContext(Context);
  const { isMobile } = useDevice();
  const dynamicLink = pageData.referral.dynamicLink.dynamicLink;

  return (
    <a
      href={dynamicLink}
      data-cy="RedeemAction"
      className={clsx(
        buttonStyles.button,
        buttonStyles.secondary,
        buttonStyles.lg,
        styles.redeemAction
      )}
      onClick={async (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        const target = event.currentTarget;
        await trackRedeemCredit();
        window.location.href = target.href;
      }}
    >
      {isMobile && <IconAdd size={24} />}
      <Text type="bodyHeavy">{t('referral.redeem')}</Text>
    </a>
  );
};

export const ReferralPage = () => {
  const { t } = useTranslation();
  const { pageData, channel } = useContext(Context);
  useTrackPageAndSessionView();

  const price = formatPrice(
    pageData.referral.promotionInfo.rewardAmountForReferee,
    channel
  );
  const referrerName = pageData.referral.referrerName;
  const bannerProps = {
    title: referrerName.name
      ? t('referral.banner.title', {
          name: referrerName.name,
          value: price,
        })
      : t('referral.meta.titleFallback', {
          amount: price,
        }),
    renderActions: RedeemAction,
  };
  const instructionStepProps = {
    title: t('referral.instructionSteps.title'),
    steps: [
      {
        header: t('referral.instructionSteps.join.header'),
        body: t('referral.instructionSteps.join.body'),
        icon: <IconHappy size={24} />,
        colorPair: {
          backgroundColor: ColorBloomTeal100,
          color: ColorBloomTeal700,
        },
      },
      {
        header: t('referral.instructionSteps.choose.header'),
        body: t('referral.instructionSteps.choose.body'),
        icon: <IconHair size={24} />,
        colorPair: {
          backgroundColor: ColorBloomPurple100,
          color: ColorBloomPurple700,
        },
      },
      {
        header: t('referral.instructionSteps.credit.header'),
        body: t('referral.instructionSteps.credit.body'),
        icon: <IconVoucher size={24} />,
        colorPair: {
          backgroundColor: ColorBloomYellow100,
          color: ColorBloomYellow700,
        },
      },
    ],
  };
  const faqProps = {
    title: t('referral.faqs.title'),
    body: <FAQBody />,
    showAllFAQsButtonLabel: t('referral.faqs.showAll'),
    faqs: pageData.referral.faqs,
  };
  return (
    <div className={styles.container} data-cy="ReferralPage">
      <TWUIReferralPage
        banner={bannerProps}
        instructionSteps={instructionStepProps}
        faqs={faqProps}
      />
    </div>
  );
};
